import React, { useEffect, useState, useContext } from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { Topography, Payment, Button, Radio } from 'components/ui';
import styles from './PaymentsSection.module.scss';
import { paymentsSelector } from 'store/reducers/paymentMethods.reducer';
import { paymentsFetch } from 'store/actions/paymentMethods';
import { PreloaderWrapper } from 'components/containers/PreloaderWrapper/PreloaderWrapper';
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import CardSection from 'components/grid/Payment/CardSection/CardSection';
import Alert from 'components/ui/Alert/Alert';
import { addNewPayment } from 'store/actions/paymentMethods';
import { HistoryContext } from 'components/contexts';
import { useNavigate } from 'hooks';
import { useFetcher } from 'utils/fetcher';
import { CustomerPaymentMethods } from 'types/stripe';
import { PaymentMethod } from '@stripe/stripe-js';
import StripeModal from 'components/ui/Modal/StripeModal';

type PaymentsSectionProps = {
  className?: string;
  selected?: string;
  addCardUrl?: string;
  onClickAddCard?: () => void;
  onSelect: (value: string, cardNumber: string, cardType: string, name: string) => void;
  hideTitle?: boolean;
  showPaymentMethods?: boolean;
  selectedPaymentMethod?: string;
  setPaymentMethod?: (value: string) => void;
  isModal?: boolean;
  rid?: number | string;
};

export const PaymentsSection: React.FC<PaymentsSectionProps> = ({
  className,
  selected,
  onSelect,
  showPaymentMethods,
  selectedPaymentMethod,
  setPaymentMethod,
  isModal,
  hideTitle = false,
  rid,
}) => {
  const dispatch = useDispatch();
  const { paymentMethods, isFetching, isUpdating } = useSelector(paymentsSelector);

  const { data, isLoading, mutate } = useFetcher<CustomerPaymentMethods>(
    `/api/v2/patient/cards/stripe_customer_payment_methods?request_id=${rid}`,
  );

  const [isStripeModalOpen, setIsStripeModalOpen] = React.useState(false);

  useEffect(() => {
    dispatch(paymentsFetch());
  }, [dispatch]);

  const selectedCard = selected && paymentMethods.find(item => item.card_id === selected);

  useEffect(() => {
    type SelectedCardProps = typeof paymentMethods[0] | undefined;
    const defaultCard: SelectedCardProps = paymentMethods.find(item => item.is_default);
    const selectedCard: SelectedCardProps = paymentMethods.find(item => item?.card_id === selected);

    if (!selected && defaultCard) onSelect(defaultCard.card_id, defaultCard.last4, defaultCard.type, defaultCard.name);
    else if (selected && selectedCard) {
      onSelect(selectedCard.card_id, selectedCard.last4, selectedCard.type, selectedCard.name || '');
    }

    if (!selected && !selectedCard && !defaultCard) onSelect('', '', '', '');
  }, [onSelect, paymentMethods, selected]);

  const handleMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMethod && setPaymentMethod(e.target.value);
  };
  if (isFetching || isUpdating) return <PreloaderWrapper />;

  return (
    <div className={clsx(styles.root, className, 'row')}>
      {(!showPaymentMethods || selectedPaymentMethod === 'credit_card') && selectedCard && selectedCard.name && (
        <div className="col-12 mb-4">
          <Topography.H4>Billing Information</Topography.H4>
          <div>{selectedCard.name}</div>
          <div>
            {selectedCard.address_line1},{selectedCard.address_line2 ? ` ${selectedCard.address_line2},` : ''}{' '}
            {selectedCard.address_city}, {selectedCard.address_state} - {selectedCard.address_zip}
          </div>
        </div>
      )}
      {!hideTitle && (
        <div className="col">
          <Topography.H4 className={styles.title}>
            {showPaymentMethods ? 'Method of Payment' : 'Select Card'}
          </Topography.H4>
        </div>
      )}
      {showPaymentMethods && (
        <>
          <div className="col-12">
            <div className="d-flex align-center">
              <Radio
                name="paymentMethod"
                value={'check'}
                checked={selectedPaymentMethod === 'check'}
                className={styles.radio}
                labelText="Check"
                onChange={handleMethod}
              />
            </div>
            <div className="d-flex align-center mt-2">
              <Radio
                name="paymentMethod"
                value={'credit_card'}
                checked={selectedPaymentMethod === 'credit_card'}
                className={styles.radio}
                labelText="Credit Card"
                onChange={handleMethod}
              />
            </div>
          </div>
        </>
      )}
      {(!showPaymentMethods || selectedPaymentMethod === 'credit_card') && (
        <div className="col-12 mt-3">
          <div className="row">
            {data && data.data.length >= 1 && !isLoading
              ? data.data.map((item: PaymentMethod | any) => (
                  <Payment.Item
                    key={item.id}
                    cardType={item.card?.brand}
                    cardNumber={item.card?.last4 || '****'}
                    radioName="payments"
                    radioValue={item.id}
                    isSelected={selected === item.id}
                    billingName={''}
                    onSelect={onSelect}
                    className={`col-12 col-md-6 col-lg-${isModal ? 6 : 4}`}
                  />
                ))
              : null}
            <StripeModal mutate={mutate} modalStates={[isStripeModalOpen, setIsStripeModalOpen]} rid={rid}/>
          </div>
        </div>
      )}
    </div>
  );
};

export const AddPaymentSection = () => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { backLink } = useContext(HistoryContext);

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    setIsSaving(true);

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (cardElement !== null) {
      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        setErrorMessage(error.message);
        setIsSaving(false);
      }

      if (token) {
        dispatch(addNewPayment(token));
        setErrorMessage(undefined);
        navigate(backLink || '/');
      }
    }
  };

  return (
    <div className={styles['form-root']}>
      <form onSubmit={handleSubmit}>
        <CardSection />
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <div className={styles['button-wrapper']}>
          <Button className={styles['button']} disabled={!stripe || isSaving} htmlType="submit">
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};
